import { useState, useEffect } from 'react';
import { Layout } from './components/layout/Layout';
import { TabNavigation } from './components/layout/TabNavigation';
import { ForSaleAdTab } from './components/forsale/ForSaleAdTab';
import { SpecialForm } from './components/forms/SpecialForm';
import { RawDisplay } from './components/specials/RawDisplay';
import SpecialCardModule from './components/specials/SpecialCardModule';
import { generateSpecial } from './services/apiService';
import type { GenerateSpecialRequest } from './services/apiService';
import { VERSION } from './version';
import { LoginScreen } from './components/auth/LoginScreen';
import './App.css';
import './global-mobile-overflow-fix.css';

function App() {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'restaurant' | 'forsale'>('restaurant');
  const [error, setError] = useState<string | null>(null);
  const [special, setSpecial] = useState<any>(null);
  const [showRawJson, setShowRawJson] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Check if user is already authenticated (could use localStorage or sessionStorage)
  useEffect(() => {
    const authStatus = sessionStorage.getItem('restaurantSpecialsAuth');
    if (authStatus === 'authenticated') {
      setIsAuthenticated(true);
    }
  }, []);
  
  const handleLogin = () => {
    // Set authentication status
    setIsAuthenticated(true);
    // Store authentication status in session storage
    sessionStorage.setItem('restaurantSpecialsAuth', 'authenticated');
  };

        const handleFormSubmit = async (data: GenerateSpecialRequest) => {
    setIsLoading(true);
    setError(null);
    
    try {
                        const generatedSpecial = await generateSpecial(data);
      setSpecial(generatedSpecial);
    } catch (err) {
      let message = 'Failed to generate special';
      if (typeof err === 'string') message = err;
      else if (err instanceof Error) message = err.message;
      else if (err && typeof err === 'object' && 'error' in err) message = String((err as any).error);
      else message = JSON.stringify(err);
      setError(message);
      console.error('Error generating special:', err);
    } finally {
      setIsLoading(false);
    }
  }

  // If not authenticated, show login screen
  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }
  
  return (
    <Layout>
      <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      <div className="mt-2">
        {activeTab === 'restaurant' && (
          <div className="space-y-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Generate Your Special</h2>
              <SpecialForm 
                onSubmit={handleFormSubmit} 
                isLoading={isLoading} 
              />
            </div>
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
                {typeof error === 'object' ? JSON.stringify(error, null, 2) : String(error)}
              </div>
            )}
            {special && (
              <>
                {showRawJson && <RawDisplay data={special} />}
                <div className="mt-8 border-t-2 border-dashed border-gray-300 pt-8">
                  <SpecialCardModule input={special} />
                </div>
              </>
            )}
            {special && (
              <div className="mt-8 flex items-center justify-center space-x-2">
                <span className="text-sm text-gray-600">Show Raw JSON</span>
                <label className="inline-flex relative items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={showRawJson}
                    onChange={() => setShowRawJson(!showRawJson)}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>
            )}
            <div className="mt-8 text-center text-xs text-gray-400">
              Version {VERSION}
            </div>
          </div>
        )}
        {activeTab === 'forsale' && (
          <ForSaleAdTab />
        )}
      </div>
    </Layout>
  );
}

export default App
